// Нова ниша › step 4: the matrix before any of it is spent (plan §3.9, Appendix D).

import { TIERS, type MatrixEstimate, type MatrixResult, type QueryTier } from '@leadg/shared';
import { TIER_LABELS } from './matrix.js';

const PAGES = 3;

function TierSummary({
  result,
  tiers,
  unticked,
  onToggleTier,
}: {
  result: MatrixResult;
  tiers: ReadonlySet<QueryTier>;
  unticked: ReadonlySet<string>;
  onToggleTier: (tier: QueryTier) => void;
}) {
  return (
    <div className="adm__tablewrap">
      <table className="adm__table">
        <thead>
          <tr>
            <th>ниво</th>
            <th className="adm__num">заявки</th>
            <th className="adm__num">избрани</th>
            <th className="adm__num">≤ извиквания</th>
            <th>примери</th>
          </tr>
        </thead>
        <tbody>
          {TIERS.filter((tier) => result.byTier[tier] > 0).map((tier) => {
            const rows = result.candidates.filter((c) => c.tier === tier);
            const chosen = tiers.has(tier) ? rows.filter((c) => !unticked.has(c.queryText)) : [];
            return (
              <tr key={tier} className={tiers.has(tier) ? '' : 'adm__row--off'}>
                <td>
                  <label className="adm__toggle-label">
                    <input
                      type="checkbox"
                      className="adm__toggle-check"
                      checked={tiers.has(tier)}
                      onChange={() => onToggleTier(tier)}
                    />
                    <strong>{TIER_LABELS[tier]}</strong>
                  </label>
                </td>
                <td className="adm__num">{rows.length}</td>
                <td className="adm__num">{chosen.length}</td>
                <td className="adm__num">{chosen.length * PAGES}</td>
                <td className="adm__sub">
                  {rows
                    .slice(0, 2)
                    .map((c) => c.queryText)
                    .join(', ')}
                  {rows.length > 2 ? ', …' : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowList({
  result,
  tiers,
  unticked,
  onToggleRow,
}: {
  result: MatrixResult;
  tiers: ReadonlySet<QueryTier>;
  unticked: ReadonlySet<string>;
  onToggleRow: (text: string) => void;
}) {
  return (
    <details className="adm__details">
      <summary className="adm__sub">Всички заявки по ред — махнете отделни</summary>
      <div className="adm__matrix-rows">
        {result.candidates
          .filter((c) => tiers.has(c.tier))
          .map((c) => (
            <label
              key={c.queryText}
              className={`adm__check ${unticked.has(c.queryText) ? 'adm__row--off' : ''}`}
            >
              <input
                type="checkbox"
                className="adm__toggle-check"
                checked={!unticked.has(c.queryText)}
                onChange={() => onToggleRow(c.queryText)}
              />
              <code>{c.queryText}</code>
              <span className="adm__pick-tag">{TIER_LABELS[c.tier]}</span>
              <span className="adm__sub">{c.areaHint ?? 'цял град'}</span>
            </label>
          ))}
      </div>
    </details>
  );
}

export function StepMatrix({
  result,
  estimate,
  cap,
  tiers,
  unticked,
  onToggleTier,
  onToggleRow,
}: {
  result: MatrixResult;
  estimate: MatrixEstimate;
  cap: number;
  tiers: ReadonlySet<QueryTier>;
  unticked: ReadonlySet<string>;
  onToggleTier: (tier: QueryTier) => void;
  onToggleRow: (text: string) => void;
}) {
  const nights = estimate.drainNights;
  return (
    <div className="adm__wizard-step">
      <h4 className="adm__h">4. Матрицата</h4>
      <p className="adm__note">
        Построена тук, в браузъра, от речника на нишата и районите на града. Нищо не е записано и
        нищо не е похарчено.
      </p>
      <TierSummary result={result} tiers={tiers} unticked={unticked} onToggleTier={onToggleTier} />
      <p className="adm__estimate">
        <strong>{estimate.queries} нови</strong>
        {result.skippedExisting.length > 0 ? (
          <> + {result.skippedExisting.length} вече съществуват</>
        ) : null}
        {' · '}≤ {estimate.maxCalls} извиквания · изтича за {nights} {nights === 1 ? 'нощ' : 'нощи'}{' '}
        при таван {cap} заявки · после ≈ {estimate.steadyQueriesPerNight} заявки/нощ
      </p>
      {result.skippedExisting.length > 0 ? (
        <details className="adm__details">
          <summary className="adm__sub">Вече съществуват ({result.skippedExisting.length})</summary>
          <p className="adm__sub">{result.skippedExisting.join(' · ')}</p>
        </details>
      ) : null}
      {result.candidates.length === 0 ? (
        <p className="adm__err">
          Матрицата е празна — нишата няма синоними или всички заявки вече съществуват.
        </p>
      ) : (
        <RowList result={result} tiers={tiers} unticked={unticked} onToggleRow={onToggleRow} />
      )}
    </div>
  );
}
